import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Size } from '@Components/designConstants';
import { useSetError } from '../../../../redux/modals';
import { exportMultiSheetToExcel } from '../../../../utils/download-utils';
import { IndicatorDot, IndicatorTooltip } from '../components/IndicatorDot';
import { ReviewControls } from '../components/ReviewControls';
import { SectionHeader } from '../components/SectionHeader';
import { buildChannelExportSheets } from '../channel-export-utils';
import { GROUP_SHORT_LABEL, SectionStatus, SectionStatusType } from '../data-qa-constants';
import { ConfigSummary, DataQaDataset, MmmConfig, SectionDiffs } from '../data-qa-types';
import { modelTypeForProductType } from '../data-qa-utils';
import { useChannelExportModels } from '../hooks/useChannelExportModels';
import {
  analyzePortfolio,
  buildChannelPills,
  buildChannelViews,
  ChannelPill,
  IndicatorReason,
  portfolioReasons,
  prefixReasons,
  reasonsIndicator,
  reasonTexts,
  sortReasons
} from '../media-inputs-utils';
import { ChannelPanel } from './ChannelPanel';
import { PortfolioPanel } from './PortfolioPanel';

interface MediaInputsSectionProps {
  config: MmmConfig;
  dataset: DataQaDataset;
  // Channels' diffs: Portfolio is the sum of the channels, so channel-weeks cover any
  // change Portfolio would show.
  diffs: SectionDiffs;
  isColdStart: boolean;
  // One decision for Portfolio and Channels together (see combineSectionStatus).
  status: SectionStatusType;
  notes: string;
  pending: boolean;
  onApprove: () => void;
  onFlag: (notes: string) => void;
  onNoteChange: (notes: string) => void;
  // Company slug (cid) -- names the table in the compare-to-canonical SQL.
  company: string;
  // See ReviewControls' approveBlockedReason.
  approveBlockedReason?: string;
  // Every config for the company -- the Channels Excel export covers every model.
  configs: ConfigSummary[];
  // Open on this channel entry (a ChannelConfig key) instead of Portfolio -- e.g. from the
  // About tab's channel table.
  initialChannelKey?: string;
}

const PORTFOLIO_PILL = 'portfolio';

const GROUP_LABEL = {
  demandGeneration: 'Demand Generation',
  demandCapture: 'Demand Capture'
} as const;

// Portfolio and Channels on one tab, one chart view at a time. Pills across the top pick
// Portfolio or a channel; each carries an indicator of what in its charts needs review.
// A channel split across Demand Generation and Demand Capture gets tabs, each with its own
// indicator, so a red pill points at the side with the problem.
export const MediaInputsSection = ({
  config,
  dataset,
  diffs,
  isColdStart,
  status,
  notes,
  pending,
  onApprove,
  onFlag,
  onNoteChange,
  company,
  approveBlockedReason,
  configs,
  initialChannelKey
}: MediaInputsSectionProps) => {
  const modelType = modelTypeForProductType(config.productType);
  const [activePill, setActivePill] = useState<string>(
    () => config.channels.find(c => c.key === initialChannelKey)?.rawLabel ?? PORTFOLIO_PILL
  );
  const [activeEntryKey, setActiveEntryKey] = useState<string | undefined>(initialChannelKey);

  const {
    loading: exportLoading,
    models: exportModels,
    failedModelLabels: exportFailedModelLabels
  } = useChannelExportModels(company, configs);
  const setError = useSetError();
  // Blocks the export when any model failed to load -- a workbook silently missing a
  // model's sheet would read as complete.
  useEffect(() => {
    if (exportFailedModelLabels.length === 0) return;
    setError({
      title: "Couldn't load every model for export",
      message: `Excel export is unavailable because ${exportFailedModelLabels.join(', ')} failed to load. Reload the page to retry.`,
      reportError: new Error(`Channel export: failed to load ${exportFailedModelLabels.join(', ')}`)
    });
  }, [exportFailedModelLabels, setError]);
  const handleExportExcel = useCallback(async () => {
    const sheets = buildChannelExportSheets(exportModels);
    await exportMultiSheetToExcel(sheets, `${company}_channel_export`);
  }, [company, exportModels]);

  const fencepostWindow = useMemo(
    () => ({
      fencepostStart: config.fencepostStart,
      fencepostEnd: config.fencepostEnd,
      reviewGranularity: dataset.reviewGranularity
    }),
    [config.fencepostStart, config.fencepostEnd, dataset.reviewGranularity]
  );
  const portfolioAnalysis = useMemo(
    () => analyzePortfolio(dataset.weeks, dataset.portfolioStats, fencepostWindow),
    [dataset.weeks, dataset.portfolioStats, fencepostWindow]
  );
  // Each channel entry's analysis and indicator, built together so a pill or tab can
  // never lack one (see buildChannelViews).
  const channelViews = useMemo(
    () => buildChannelViews(config, dataset, fencepostWindow),
    [config, dataset, fencepostWindow]
  );
  const reasonsFor = (channelKey: string): IndicatorReason[] =>
    channelViews.get(channelKey)?.reasons ?? [];
  // A split channel's pill carries both sides' reasons, each marked with its side.
  const pillReasons = (pill: ChannelPill): IndicatorReason[] =>
    pill.entries.length > 1
      ? sortReasons(
          pill.entries.flatMap(e => prefixReasons(reasonsFor(e.key), GROUP_SHORT_LABEL[e.group]))
        )
      : reasonsFor(pill.entries[0].key);
  const portfolioPillReasons = useMemo(
    () =>
      portfolioReasons(
        portfolioAnalysis,
        (dataset.negativeValues ?? []).filter(f => f.kind === 'portfolio')
      ),
    [portfolioAnalysis, dataset.negativeValues]
  );

  const pills = useMemo(() => buildChannelPills(config.channels), [config.channels]);
  // A pill from a previously viewed model that this one doesn't have falls back to
  // Portfolio.
  const activeChannelPill = pills.find(p => p.label === activePill);
  const activeEntry =
    activeChannelPill?.entries.find(e => e.key === activeEntryKey) ?? activeChannelPill?.entries[0];
  const activeView = activeEntry ? channelViews.get(activeEntry.key) : undefined;

  // "<rawLabel>::<group>" -> the raw feature_id(s) behind it, for a split channel's
  // compare-to-canonical SQL filter (see ChannelPanel/GroundTruthSqlPanel).
  const featureIdsByChannelGroup = useMemo(() => {
    const map = new Map<string, string[]>();
    config.featureMapping.forEach(row => {
      if (!row.featureId) return;
      const mapKey = `${row.channel}::${row.group}`;
      map.set(mapKey, [...(map.get(mapKey) ?? []), ...row.featureId.split(', ')]);
    });
    return map;
  }, [config.featureMapping]);

  // Reviewed count comes from the channel-week fingerprint diff, gated on APPROVED -- a
  // flagged review isn't done.
  const reviewedWeekCount = status === SectionStatus.APPROVED ? diffs.summary.unchanged : 0;

  const selectPill = (pill: string) => {
    setActivePill(pill);
    setActiveEntryKey(undefined);
  };

  return (
    <div className='data-qa-channels data-qa-media-inputs'>
      <SectionHeader
        title='Media Inputs (Client Partner and Channel Leads to Review)'
        modelType={modelType}
        subtitle='Total and per-channel spend and impressions, compared with the canonical Bliss Point database'
        tips={
          <>
            <li>
              <strong>Indicators</strong>: red needs investigating (MMM more than 1% off BPM, or a
              negative value that blocks training), yellow is worth noting (0.5–1% off, weak
              spend-vs-impressions correlation, or a negative-value warning), grey has no BPM
              comparison, green has no issues
            </li>
            <li>
              <strong>Spend &amp; Impressions trends</strong>: Look for unexpected gaps, sudden
              drops to zero, or extreme spikes
            </li>
            <li>
              <strong>Correlation &gt; 0.7</strong> between spend and impressions is expected; low
              correlation may indicate data mapping issues
            </li>
            <li>
              <strong>CPM</strong>: Check that CPM values are reasonable for each platform (e.g.,
              social ~$5-15, search ~$20-50)
            </li>
            <li>
              <strong>Flag</strong> if you see large unexplained gaps, low correlation or
              unreasonable CPMs
            </li>
          </>
        }
      />

      <div className='data-qa-media-inputs__pill-row'>
        <Nav
          variant='pills'
          className='data-qa-pills'
          activeKey={activeChannelPill ? activeChannelPill.label : PORTFOLIO_PILL}
          onSelect={key => key && selectPill(key)}
        >
          <IndicatorPill eventKey={PORTFOLIO_PILL} reasons={portfolioPillReasons}>
            Portfolio
          </IndicatorPill>
          {pills.map(pill => (
            <IndicatorPill key={pill.label} eventKey={pill.label} reasons={pillReasons(pill)}>
              {pill.label}
            </IndicatorPill>
          ))}
        </Nav>
        <DownloadDropdown
          size={Size.SM}
          disabled={
            exportLoading || exportModels.length === 0 || exportFailedModelLabels.length > 0
          }
          menuOptions={['XLSX']}
          onClickOptions={[handleExportExcel]}
        />
      </div>

      {activeChannelPill && activeEntry && activeView ? (
        <div className='data-qa-media-inputs__view'>
          {activeChannelPill.entries.length > 1 ? (
            <Nav
              variant='pills'
              className='data-qa-channel-tabs'
              activeKey={activeEntry.key}
              onSelect={key => key && setActiveEntryKey(key)}
            >
              {activeChannelPill.entries.map(entry => (
                <IndicatorPill key={entry.key} eventKey={entry.key} reasons={reasonsFor(entry.key)}>
                  {GROUP_LABEL[entry.group]}
                </IndicatorPill>
              ))}
            </Nav>
          ) : (
            <span className='data-qa-media-inputs__group'>{GROUP_LABEL[activeEntry.group]}</span>
          )}
          <ChannelPanel
            key={activeEntry.key}
            channel={activeEntry}
            analysis={activeView.analysis}
            stats={dataset.channelStats.find(c => c.channelKey === activeEntry.key)}
            reviewGranularity={dataset.reviewGranularity}
            bookmarkWeekIso={diffs.bookmarkWeekIso}
            isColdStart={isColdStart}
            company={company}
            featureIds={
              featureIdsByChannelGroup.get(`${activeEntry.rawLabel}::${activeEntry.group}`) ?? []
            }
          />
        </div>
      ) : (
        <PortfolioPanel
          dataset={dataset}
          analysis={portfolioAnalysis}
          bookmarkWeekIso={diffs.bookmarkWeekIso}
          isColdStart={isColdStart}
          company={company}
        />
      )}

      <ReviewControls
        status={status}
        notes={notes}
        pending={pending}
        onApprove={onApprove}
        onFlag={onFlag}
        onNoteChange={onNoteChange}
        approveLabel='Approve Media Inputs'
        flagLabel='Flag Media Inputs'
        approveBlockedReason={approveBlockedReason}
        summary={diffs.summary}
        isColdStart={isColdStart}
        reviewed={reviewedWeekCount}
        total={dataset.weeks.length}
        unit='channel-weeks'
      />
    </div>
  );
};

interface IndicatorPillProps {
  eventKey: string;
  reasons: IndicatorReason[];
  children: ReactNode;
}

// A pill or tab with its indicator dot. The whole pill carries the dot's hover text, not
// just the dot, so it shows wherever the pill is pointed at.
const IndicatorPill = ({ eventKey, reasons, children }: IndicatorPillProps) => {
  const indicator = reasonsIndicator(reasons);
  const texts = reasonTexts(reasons);

  return (
    <Nav.Item>
      <IndicatorTooltip indicator={indicator} reasons={texts} hideWhenClear>
        <Nav.Link eventKey={eventKey}>
          <IndicatorDot indicator={indicator} reasons={texts} tooltip={false} />
          {children}
        </Nav.Link>
      </IndicatorTooltip>
    </Nav.Item>
  );
};
